import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { PublicClientApplication } from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { NotificationService } from '../notification/notification.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private initialized = false;
  private isProcessingAuth = false;
  private loginAttemptInProgress = false;

  constructor(
    @Inject(MSAL_INSTANCE) private readonly msal: PublicClientApplication,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly notificationService: NotificationService,
  ) {}

  private async ensureInit(): Promise<void> {
    if (!this.initialized) {
      await this.msal.initialize();
      this.initialized = true;
    }
  }

  async initAuthFlow(): Promise<void> {
    if (this.isProcessingAuth) {
      return;
    }

    const currentUrl = globalThis.location.pathname;
    if (currentUrl === '/login' || currentUrl === '/redefinir-senha') {
      return;
    }

    const hasAuthFragment =
      globalThis.location.hash.includes('code=') ||
      globalThis.location.hash.includes('error=');
    const hasError = globalThis.location.hash.includes('error=');

    if (hasError) {
      globalThis.history.replaceState(
        {},
        document.title,
        globalThis.location.pathname,
      );
      await this.clearAuthCache();
      this.notificationService.error('Erro na autenticação com Azure');
      return;
    }

    this.isProcessingAuth = true;

    try {
      await this.ensureInit();
      const redirectResult = await this.msal.handleRedirectPromise();

      if (!redirectResult && hasAuthFragment && !hasError) {
        globalThis.history.replaceState(
          {},
          document.title,
          globalThis.location.pathname,
        );
        this.isProcessingAuth = false;
        await this.clearAuthCache();
        setTimeout(() => this.login(), 1000);
        return;
      }

      if (redirectResult) {
        const azureToken = redirectResult.accessToken;

        if (!azureToken) {
          throw new Error('Token não disponível após redirect');
        }

        await this.sendTokenToBackend(azureToken);
        globalThis.history.replaceState(
          {},
          document.title,
          globalThis.location.pathname,
        );
        this.isProcessingAuth = false;
        this.loginAttemptInProgress = false;
        await this.router.navigate(['/']);
        return;
      }

      const accounts = this.msal.getAllAccounts();

      if (accounts.length === 0) {
        this.isProcessingAuth = false;
        return;
      }

      // Se há conta em cache, tentar obter token silenciosamente
      try {
        const silentRequest = {
          scopes: [`api://${environment.azureClientIdApi}/Group.Read.All`],
          account: accounts[0],
          forceRefresh: false,
        };

        const tokenResult = await this.msal.acquireTokenSilent(silentRequest);
        await this.sendTokenToBackend(tokenResult.accessToken);

        this.isProcessingAuth = false;
        await this.router.navigate(['/']);
      } catch (error) {
        this.handleAuthError(error);
        await this.clearAuthCache();
        this.isProcessingAuth = false;
      }
    } catch (error) {
      this.handleAuthError(error);
      await this.clearAuthCache();
      this.isProcessingAuth = false;
      this.loginAttemptInProgress = false;
    }
  }

  async login(): Promise<void> {
    if (this.loginAttemptInProgress) {
      return;
    }

    this.loginAttemptInProgress = true;

    try {
      await this.ensureInit();
      await this.clearAuthCache();

      await this.msal.loginRedirect({
        scopes: [`api://${environment.azureClientIdApi}/Group.Read.All`],
        authority: `https://login.microsoftonline.com/${environment.azureTenantId}`,
        redirectUri: environment.azureRedirectUri,
        prompt: 'select_account', // Permite SSO - não força login toda vez
      });
    } catch (error) {
      console.error('Erro ao tentar login via Azure:', error);
      this.loginAttemptInProgress = false;
      await this.clearAuthCache();
      this.isProcessingAuth = false;
    }
  }

  private async sendTokenToBackend(azureToken: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.apiBaseUrl}/auth/login`, {
          token: azureToken,
        }),
      );

      if (response?.data) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        localStorage.setItem('expiresIn', response.data.expiresIn.toString());
        localStorage.setItem(
          'refreshExpiresIn',
          response.data.refreshExpiresIn.toString(),
        );
        localStorage.setItem('loginTime', Date.now().toString());
      }
    } catch (error: any) {
      this.handleAuthError(error);
      throw error;
    }
  }

  async refreshAccessToken(): Promise<string> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        await this.ensureInit();
        const accounts = this.msal.getAllAccounts();

        if (accounts.length === 0) {
          throw new Error('Nenhuma conta autenticada');
        }

        await this.msal.clearCache();

        const tokenResult = await this.msal.acquireTokenSilent({
          scopes: [`api://${environment.azureClientIdApi}/Group.Read.All`],
          account: accounts[0],
          forceRefresh: true,
        });

        await this.sendTokenToBackend(tokenResult.accessToken);

        const newAccessToken = localStorage.getItem('accessToken');
        if (!newAccessToken) {
          throw new Error('Falha ao obter novo token');
        }

        return newAccessToken;
      }

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.apiBaseUrl}/auth/refresh`, {
          refreshToken: refreshToken,
        }),
      );

      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('expiresIn', response.expiresIn);
      localStorage.setItem('refreshExpiresIn', response.refreshExpiresIn);
      localStorage.setItem('loginTime', Date.now().toString());

      return response.accessToken;
    } catch (error) {
      console.error('? Erro ao renovar token:', error);
      this.notificationService.error('Sessão expirada. Faça login novamente.');
      await this.logout();
      throw error;
    }
  }

  private handleAuthError(error: any): void {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 0:
          this.notificationService.error(
            'Não foi possível conectar ao servidor',
          );
          break;
        case 401: {
          const message = error.error?.message || 'Token inválido';
          this.notificationService.error(message);
          break;
        }
        case 403:
          this.notificationService.error('Sem permissão');
          break;
        case 500:
          this.notificationService.error('Erro no servidor');
          break;
        default:
          this.notificationService.error(
            error.error?.message || 'Erro ao autenticar',
          );
      }
    } else {
      this.notificationService.error('Erro inesperado');
    }
  }

  async logout(): Promise<void> {
    this.isProcessingAuth = false;
    this.loginAttemptInProgress = false;
    await this.clearAuthCache();
    this.notificationService.info('Logout realizado com sucesso!');

    try {
      await this.msal.logoutRedirect({
        postLogoutRedirectUri: environment.azureRedirectUri,
      });
    } catch (error) {
      console.error('Erro ao realizar logout redirect:', error);
      globalThis.location.href = environment.azureRedirectUri;
    }
  }

  private async clearAuthCache(): Promise<void> {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresIn');
    localStorage.removeItem('refreshExpiresIn');
    localStorage.removeItem('loginTime');

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('msal.') ||
          key.includes('login.windows.net') ||
          key.includes('login.microsoftonline.com') ||
          key.includes(environment.azureClientIdSpa) ||
          key.includes(environment.azureClientIdApi))
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));

    try {
      const accounts = this.msal.getAllAccounts();
      if (accounts.length > 0) {
        await this.msal.clearCache();
      }
    } catch (error) {
      console.warn('Erro ao limpar cache MSAL:', error);
    }
  }

  getAccount(): any {
    try {
      const active = this.msal.getActiveAccount();
      if (active) return active;
      const all = this.msal.getAllAccounts();
      return all && all.length > 0 ? all[0] : null;
    } catch {
      return null;
    }
  }

  getUserName(): string | null {
    return this.getAccount()?.name ?? null;
  }

  getUserEmail(): string | null {
    return this.getAccount()?.username ?? null;
  }

  get isLoginEnabled(): boolean {
    return environment.enableLogin;
  }

  get isAuthenticated(): boolean {
    const accessToken = localStorage.getItem('accessToken');
    const expiresIn = localStorage.getItem('expiresIn');
    const loginTime = localStorage.getItem('loginTime');

    if (!accessToken || !expiresIn || !loginTime) {
      return false;
    }

    const currentTime = Date.now();
    const tokenExpireTime =
      Number.parseInt(loginTime) + Number.parseInt(expiresIn) * 1000;

    return currentTime < tokenExpireTime;
  }
}
